import { useEffect, useState, type ReactNode } from 'react';
import { ClipboardList, Dog, ExternalLink, FileText, Mail, Paintbrush, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import { adminApi, type Overview as OverviewData } from '../../lib/adminApi';
import { useCms } from './CmsContext';

const SITE_MAP: Array<{ label: string; editor: string; anchor: string; note: string }> = [
  { label: 'Navigation & hero', editor: '/admin/design?section=hero', anchor: '/#top', note: 'Headline, calls to action, trust bar, and hero media' },
  { label: 'Our dogs', editor: '/admin/design?section=dogs', anchor: '/#dogs', note: 'Lineage, biographies, clearances, and photos' },
  { label: 'Health standards', editor: '/admin/design?section=health', anchor: '/#health', note: 'Testing commitments and health records' },
  { label: 'Upcoming litters', editor: '/admin/litters', anchor: '/#litters', note: 'Pairings, availability, dates, and cover images' },
  { label: 'Puppy application', editor: '/admin/form', anchor: '/apply', note: 'Applicant steps, questions, and required fields' },
  { label: 'Frequently asked', editor: '/admin/design?section=faq', anchor: '/#faq', note: 'Common family questions and answers' },
];

const number = new Intl.NumberFormat('en-US');

function Metric({ label, value, to, icon }: { label: string; value: number; to: string; icon: ReactNode }) {
  return (
    <Link to={to} className="cms-tile">
      <span className="cms-tile-top"><span className="cms-tile-label">{label}</span>{icon}</span>
      <span className="cms-tile-value">{number.format(value)}</span>
    </Link>
  );
}

export function Overview() {
  const { content, litters, steps } = useCms();
  const [data, setData] = useState<OverviewData | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    adminApi.getOverview().then(setData).catch(() => setFailed(true));
  }, []);

  if (failed) return <p className="cms-empty">Could not load your dashboard. Refresh to try again.</p>;
  if (!data) return <div className="cms-page cms-page-loading"><span className="cms-skeleton cms-skeleton-title" /><span className="cms-skeleton cms-skeleton-card" /></div>;

  const sectionOrder = content.sections?.order ?? [];
  const hiddenSections = content.sections?.hidden ?? [];
  const visibleSections = sectionOrder.filter((section) => !hiddenSections.includes(section)).length;

  return (
    <div className="cms-page">
      <div className="cms-home-head">
        <div>
          <h1>Home</h1>
          <p>Manage the Sugarloaf site, applications, litters, and family communication.</p>
        </div>
        <div className="cms-home-actions">
          <a href="/" target="_blank" rel="noreferrer" className="cms-btn cms-btn-ghost">View site <ExternalLink size={14} /></a>
          <a href="/admin/design" className="cms-btn cms-btn-primary"><Paintbrush size={15} />Customize</a>
        </div>
      </div>

      <section className="cms-store-card" aria-labelledby="cms-online-store-title">
        <header className="cms-store-card-head">
          <div className="cms-store-card-title">
            <span><Paintbrush size={18} /></span>
            <span><h2 id="cms-online-store-title">Online store</h2><small>Sugarloaf theme · Home page</small></span>
          </div>
          <span className="cms-live-pill">Live</span>
        </header>
        <div className="cms-store-preview">
          <iframe src="/?cms-preview=home" title="Live Sugarloaf homepage preview" loading="lazy" tabIndex={-1} />
          <div className="cms-store-preview-shade" />
        </div>
        <footer className="cms-store-card-foot">
          <div className="cms-store-meta">
            <b>Your site design is the center of this CMS</b>
            <span>{visibleSections} visible homepage sections · {litters.length} litter records · {steps.length} application steps</span>
          </div>
          <div className="cms-store-actions">
            <Link to="/admin/content" className="cms-btn cms-btn-ghost"><FileText size={14} />Content</Link>
            <a href="/admin/design" className="cms-btn cms-btn-primary"><Paintbrush size={14} />Edit design</a>
          </div>
        </footer>
      </section>

      <div className="cms-tiles" aria-label="Business activity">
        <Metric label="New applications" value={data.counts.newApplications} to="/admin/applications" icon={<ClipboardList />} />
        <Metric label="Unread messages" value={data.counts.unreadEmails} to="/admin/inbox" icon={<Mail />} />
        <Metric label="Live litters" value={data.counts.liveLitters} to="/admin/litters" icon={<Dog />} />
        <Metric label="Subscribers" value={data.counts.subscribers} to="/admin/newsletter" icon={<Users />} />
      </div>

      <section className="cms-section">
        <h2>Manage your site</h2>
        <div className="cms-sitemap">
          {SITE_MAP.map((entry) => (
            <article key={entry.label} className="cms-sitemap-card">
              <div>
                <h3>{entry.label}</h3>
                <p>{entry.note}</p>
              </div>
              <div className="cms-sitemap-actions">
                {entry.editor.startsWith('/admin/design') ? <a href={entry.editor} className="cms-btn cms-btn-ghost">Edit</a> : <Link to={entry.editor} className="cms-btn cms-btn-ghost">Edit</Link>}
                <a href={entry.anchor} target="_blank" rel="noreferrer" className="cms-link" aria-label={`View ${entry.label} on the live site`}>View</a>
              </div>
            </article>
          ))}
        </div>
      </section>

      <div className="cms-two-col">
        <section className="cms-section">
          <h2>Recent applications</h2>
          {data.recentApplications.length ? (
            <ul className="cms-activity">
              {data.recentApplications.map((application) => (
                <li key={application.id}>
                  <Link to={`/admin/applications?id=${encodeURIComponent(application.id)}`}>
                    <span className="cms-activity-title">{application.applicantName}</span>
                    <span className={`cms-status cms-status-${application.status}`}>{application.status}</span>
                  </Link>
                  <time>{new Date(application.submittedAt).toLocaleDateString()}</time>
                </li>
              ))}
            </ul>
          ) : <p className="cms-empty">No applications yet — new families will appear here.</p>}
        </section>

        <section className="cms-section">
          <h2>Recent email</h2>
          {data.recentEmails.length ? (
            <ul className="cms-activity">
              {data.recentEmails.map((email) => (
                <li key={email.id}>
                  <Link to={`/admin/inbox?thread=${encodeURIComponent(email.threadId)}`}>
                    <span className="cms-activity-title">{email.direction === 'inbound' ? (email.fromName || email.fromEmail) : `To ${email.toEmail}`}</span>
                    <span className="cms-activity-sub">{email.subject}</span>
                  </Link>
                  <time>{new Date(email.createdAt).toLocaleDateString()}</time>
                </li>
              ))}
            </ul>
          ) : <p className="cms-empty">No email yet. Connect Resend inbound in Settings.</p>}
        </section>
      </div>
    </div>
  );
}
